import { AccessTokenPayload } from '../../../../core/models/auth/access-token-payload.model';
import { UtilService } from '../../../../core/services/util.service';
import { Session } from '../../../../core/models/auth/session.model';
import { useAuthStore } from '../../../../core/store/auth.store';
import { AuthService } from '../../services/auth-login.service';
import React, { useEffect, useRef, useState } from 'react';
import { useRouteStore } from '../../../../core/store/route.store';
import { User } from '../../models/user.model';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Input, InputRef, Spin } from 'antd';
import {
  EyeInvisibleOutlined,
  EyeOutlined,
  CheckCircleTwoTone,
  CloseCircleTwoTone,
} from '@ant-design/icons';
import { route } from '../../../../constants/routes';
import { jo } from '../../../../theme/css-vars';

const PasswordSetup: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setAuth } = useAuthStore();
  const [user, setUser] = useState<User>(new User());
  const [confirmPassword, setConfirmPassword] = useState('');
  const [id, setId] = useState('');
  const passwordRef = useRef<InputRef>(null);
  const confirmPasswordRef = useRef<InputRef>(null);
  const [loading, setLoading] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const { setRoutePrefix } = useRouteStore();

  useEffect(() => {
    if (passwordRef.current) {
      passwordRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const code = searchParams.get('code');
    if (!code) {
      UtilService.Alert('Error', 'error', 'Invalid verification link');
      navigate('/login');
      return;
    }

    AuthService.GetInfo(code)
      .then((userData) => {
        if (!userData?.email || !userData?.id) {
          UtilService.Alert('Error', 'error', 'Invalid verification code');
          navigate('/login');
          return;
        }
        setUser({ ...new User(), email: userData.email });
        setId(userData.id);
      })
      .catch(() => navigate('/login'));
  }, [navigate, searchParams]);

  const HandleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setUser((prevUser) => ({ ...prevUser, [name]: value }));
  };

  const Validate = (user: User, callback: () => void) => {
    setHasSubmitted(true);
    const email = user.email || '';
    const password = user.password || '';

    const rules = [
      password.length >= 8,
      /[A-Z]/.test(password),
      /[a-z]/.test(password),
      /[0-9]/.test(password),
      /[!@#$%^&*(),.?":{}|<>]/.test(password),
      password !== email,
    ];

    const allRulesPassed = rules.every((r) => r === true);
    if (!allRulesPassed) {
      return;
    }

    if (!confirmPassword) {
      UtilService.Alert('Important!', 'info', 'You must confirm your password.');
      return;
    }

    if (password !== confirmPassword) {
      UtilService.Alert('Important!', 'info', 'Passwords do not match.');
      return;
    }

    callback();
  };

  const SetupPassword = async () => {
    setLoading(true);
    const code = searchParams.get('code');
    if (!code || !id) {
      UtilService.Alert('Error', 'error', 'Invalid verification code or user ID');
      setLoading(false);
      return;
    }
    try {
      const item = await AuthService.SetupPassword(id, user.email, code, user.password);
      if (item) {
        const loginUser = new User();
        loginUser.username = user.email;
        loginUser.password = user.password;

        const loginResponse = await AuthService.Login(loginUser);
        if (loginResponse) {
          const payload: AccessTokenPayload = UtilService.DecodeJWT(loginResponse.accessToken);

          const session: Session = {
            session_state: payload.session_state,
            realm_access: payload.realm_access,
            resource_access: payload.resource_access,
            scope: payload.scope,
            sid: payload.sid,
            email_verified: payload.email_verified,
            name: payload.name,
            preferred_username: payload.preferred_username,
            given_name: payload.given_name,
            family_name: payload.family_name,
            email: payload.email,
          };

          localStorage.clear();
          localStorage.setItem('token', loginResponse.accessToken);
          setAuth(loginResponse, payload, session);
          setRoutePrefix('/admin');
          navigate(route('/account-activation'));
        }
      }
    } catch (error) {
      UtilService.Alert('Error', 'error', 'Failed to set up password or login.');
    } finally {
      setLoading(false);
    }
  };

  const passwordValidation = [
    {
      rule: user.password?.length >= 8,
      message: 'At least 8 characters',
    },
    {
      rule: /[A-Z]/.test(user.password || ''),
      message: 'At least 1 uppercase letter',
    },
    {
      rule: /[a-z]/.test(user.password || ''),
      message: 'At least 1 lowercase letter',
    },
    {
      rule: /[0-9]/.test(user.password || ''),
      message: 'At least 1 number',
    },
    {
      rule: /[!@#$%^&*(),.?":{}|<>]/.test(user.password || ''),
      message: 'At least 1 special character (!@#$...)',
    },
    {
      rule: user.password && user.email && user.password !== user.email,
      message: 'Cannot be the same as email',
    },
  ];

  return (
    <div className='flex min-h-screen flex-col justify-center px-6 py-12 lg:px-8'>
      <div className='max-w-[320px] w-full m-auto'>
        <div className='sm:mx-auto w-full'>
          <img
            className='mx-auto h-14 w-auto'
            src={require('../../../../assets/logo.png')}
            alt='Logo'
          />
          <h2 className='mt-4 text-center text-2xl/9 font-bold tracking-tight text-gray-900'>
            Setup Your Credentials
          </h2>
        </div>

        <div className='mt-10 sm:mx-auto w-full'>
          <div className='space-y-10'>
            <div className='space-y-4'>
              <div>
                <label htmlFor='email' className='block text-sm/6 font-medium text-gray-900'>
                  Username
                </label>
                <div className='mt-2'>
                  <input
                    id='email'
                    name='email'
                    type='email'
                    autoComplete='email'
                    readOnly
                    value={user.email}
                    className='block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 bg-gray-50 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm/6 px-3'
                  />
                </div>
              </div>

              <div>
                <label htmlFor='password' className='block text-sm/6 font-medium text-gray-900'>
                  New Password
                </label>
                <div className='mt-2'>
                  <Input.Password
                    id='password'
                    name='password'
                    autoComplete='new-password'
                    required
                    placeholder='Enter your password...'
                    ref={passwordRef}
                    value={user.password}
                    onChange={HandleInput}
                    disabled={loading}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && confirmPasswordRef?.current) {
                        confirmPasswordRef.current.focus();
                      }
                    }}
                    iconRender={(visible) =>
                      visible ? (
                        <EyeOutlined style={{ color: jo.primary }} />
                      ) : (
                        <EyeInvisibleOutlined style={{ color: jo.textDisabled }} />
                      )
                    }
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor='confirmPassword'
                  className='block text-sm/6 font-medium text-gray-900'>
                  Confirm Password
                </label>
                <div className='mt-2'>
                  <Input.Password
                    id='confirmPassword'
                    name='confirmPassword'
                    autoComplete='new-password'
                    required
                    placeholder='Confirm your password...'
                    ref={confirmPasswordRef}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={loading}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        Validate(user, SetupPassword);
                      }
                    }}
                    iconRender={(visible) =>
                      visible ? (
                        <EyeOutlined style={{ color: jo.primary }} />
                      ) : (
                        <EyeInvisibleOutlined style={{ color: jo.textDisabled }} />
                      )
                    }
                  />
                </div>
              </div>
            </div>

            <div className='space-y-1 text-sm'>
              {passwordValidation.map((item, index) => {
                let icon;

                if (!hasSubmitted) {
                  icon = (
                    <span className='inline-block w-4 h-4 rounded-full border border-gray-400 mr-2'></span>
                  );
                } else if (item.rule) {
                  icon = <CheckCircleTwoTone twoToneColor={jo.success} className='mr-1' />;
                } else {
                  icon = <CloseCircleTwoTone twoToneColor={jo.danger} className='mr-1' />;
                }

                const textColor = !hasSubmitted
                  ? 'text-black'
                  : item.rule
                  ? 'text-green-600'
                  : 'text-red-500';

                return (
                  <div key={index} className={`flex items-center ${textColor}`}>
                    {icon}
                    {item.message}
                  </div>
                );
              })}
            </div>
            <button
                onClick={() => Validate(user, SetupPassword)}
                disabled={loading}
                className='flex w-full justify-center rounded-md bg-blue-600 px-3 py-1.5 text-sm/6 font-semibold text-white shadow-sm 
                hover:bg-blue-500 
                focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 
                disabled:bg-blue-100 disabled:cursor-not-allowed'>
                {loading ? <Spin /> : 'Sign In'}
              </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordSetup;
